import { GateDError } from './errors.js';

export const CATEGORY_INDEX_MIN_POSTS = 8;

export const GATE_D_CATEGORIES = Object.freeze([
  Object.freeze({ mediaType: 'video', slug: 'model-feature-showcase', label: 'Model / Feature Showcase', description: 'AI video model releases, feature demonstrations, and capability showcases.' }),
  Object.freeze({ mediaType: 'video', slug: 'cinematic-scene-showcase', label: 'Cinematic Scene Showcase', description: 'Cinematic AI video scenes, camera language, and visual storytelling.' }),
  Object.freeze({ mediaType: 'video', slug: 'vlog-social-lifestyle', label: 'Vlog / Social Lifestyle', description: 'Social-first AI video ideas for vlogs, creators, and everyday culture.' }),
  Object.freeze({ mediaType: 'video', slug: 'short-film-story', label: 'Short Film / Story', description: 'Narrative AI shorts, story-driven scenes, and compact visual storytelling.' }),
  Object.freeze({ mediaType: 'video', slug: 'anime-animation', label: 'Anime / Animation', description: 'Animated AI video concepts, characters, and stylized worlds.' }),
  Object.freeze({ mediaType: 'video', slug: 'product-ad-ecommerce', label: 'Product Ad / E-commerce', description: 'AI product films, commercial concepts, and e-commerce video creatives.' }),
  Object.freeze({ mediaType: 'video', slug: 'avatar-talking-lip-sync', label: 'Avatar / Talking / Lip-sync', description: 'Talking avatars, presenter videos, and lip-sync performances.' }),
  Object.freeze({ mediaType: 'video', slug: 'camera-effect-motion', label: 'Camera / Effect / Motion', description: 'Camera moves, visual effects, transitions, and motion experiments.' }),
  Object.freeze({ mediaType: 'video', slug: 'music-dance-performance', label: 'Music / Dance / Performance', description: 'AI music visuals, dance sequences, and performance-led videos.' }),
  Object.freeze({ mediaType: 'image', slug: 'model-feature-showcase', label: 'Model / Feature Showcase', description: 'AI image model releases, feature demonstrations, and capability showcases.' }),
  Object.freeze({ mediaType: 'image', slug: 'profile-avatar', label: 'Profile / Avatar', description: 'AI profile images, avatars, and creator identity concepts.' }),
  Object.freeze({ mediaType: 'image', slug: 'social-media-post', label: 'Social Media Post', description: 'AI visuals designed for social posts and campaigns.' }),
  Object.freeze({ mediaType: 'image', slug: 'infographic-education', label: 'Infographic / Education', description: 'Clear AI-generated educational graphics, diagrams, and explainers.' }),
  Object.freeze({ mediaType: 'image', slug: 'poster-brand-ad-design', label: 'Poster / Brand / Ad Design', description: 'AI posters, brand visuals, advertising concepts, and campaign design.' }),
  Object.freeze({ mediaType: 'image', slug: 'anime-manga-illustration', label: 'Anime / Manga / Illustration', description: 'AI anime, manga, editorial illustration, and stylized artwork.' }),
  Object.freeze({ mediaType: 'image', slug: 'photography-cinematic-still', label: 'Photography / Cinematic Still', description: 'AI photography, cinematic frames, and polished still imagery.' }),
  Object.freeze({ mediaType: 'image', slug: 'unknown-other', label: 'Unknown / Other', description: 'Distinctive AI image creations that do not fit another primary category.' }),
]);

const freezeFacet = (values) => Object.freeze(values.map((label) => Object.freeze({
  label,
  value: label.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
})));

/**
 * Explore Phase 1 controlled taxonomy from the product requirements.
 * The UI uses the same values for filters and search suggestions so taxonomy
 * never forks into a second hard-coded component list.
 */
export const EXPLORE_CONTENT_FACETS = Object.freeze({
  video: Object.freeze({
    useCases: freezeFacet([
      'Cinematic Scene Showcase', 'Vlog / Social Lifestyle', 'Short Film',
      'Music Video', 'Brand / Product Commercial', 'UGC / Talking Head Ad',
      'Virtual Try-On', 'Explainer / Tutorial', 'Channel Intro / Brand Asset',
      'Game Cinematic / PV', 'Storytelling',
    ]),
    styles: freezeFacet([
      'Cinematic Realistic', 'Fantasy / Magical', 'Cyberpunk / Sci-Fi', 'Anime',
      'Vintage / Retro Film', 'Surreal / Dreamlike', 'Documentary', '3D Cartoon',
      '2D Animation', 'Chinese Ink', 'Stop Motion',
    ]),
    subjects: freezeFacet([
      'Person / Character', 'Sports / Action', 'Nature / Landscape',
      'Architecture / Interior', 'Abstract / VFX', 'Cityscape / Urban',
      'Animal / Pet', 'Vehicle', 'Text / Logo', 'Crowd / Group',
      'Food / Drink', 'Product',
    ]),
  }),
  image: Object.freeze({
    useCases: freezeFacet([
      'Profile / Avatar', 'Social Media Post', 'Infographic / Edu Visual',
      'YouTube Thumbnail', 'Comic / Storyboard', 'Product Marketing',
      'E-commerce Main Image', 'Game Asset', 'Poster / Flyer', 'App / Web Design',
    ]),
    styles: freezeFacet([
      'Photography', 'Cinematic / Film Still', 'Anime / Manga', 'Illustration',
      'Sketch / Line Art', 'Comic / Graphic Novel', '3D Render', 'Chibi / Q-Style',
      'Isometric', 'Pixel Art', 'Oil Painting', 'Watercolor', 'Ink / Chinese Style',
      'Retro / Vintage', 'Cyberpunk / Sci-Fi', 'Minimalism',
    ]),
    subjects: freezeFacet([
      'Portrait / Selfie', 'Influencer / Model', 'Character', 'Group / Couple',
      'Product', 'Food / Drink', 'Fashion Item', 'Animal / Creature', 'Vehicle',
      'Architecture / Interior', 'Landscape / Nature', 'Cityscape / Street',
      'Diagram / Chart', 'Text / Typography', 'Abstract / Background',
    ]),
  }),
});

export const EXPLORE_FEATURE_FACETS = Object.freeze({
  video: freezeFacet([
    'Text to Video', 'Image to Video', 'Reference to Video', 'Frames to Video',
    'AI Video Editor', 'AI Video Extender',
  ]),
  image: freezeFacet(['Text to Image', 'Image to Image', 'AI Photo Editor']),
});

export function getCategory(mediaType, slug) {
  const category = GATE_D_CATEGORIES.find((item) => (
    item.mediaType === mediaType && item.slug === slug
  ));
  if (!category) {
    throw new GateDError('CATEGORY_NOT_FOUND', 'Category not found.', 404);
  }
  return category;
}

export function categoryPath({ locale = 'en', mediaType, slug }) {
  const category = getCategory(mediaType, slug);
  return `/${locale}/explore/${category.mediaType}/${category.slug}`;
}

export function categoryIndexability({
  approvedPrimaryCount,
  minimum = CATEGORY_INDEX_MIN_POSTS,
} = {}) {
  const count = Number(approvedPrimaryCount || 0);
  return Object.freeze({
    indexable: Number.isInteger(count) && count >= minimum,
    count,
    minimum,
    robots: count >= minimum ? 'index,follow' : 'noindex,follow',
  });
}

export function categoryStructuredData({
  siteUrl,
  locale = 'en',
  mediaType,
  slug,
  items = [],
  categoryLabel = null,
  categoryDescription = null,
} = {}) {
  const category = getCategory(mediaType, slug);
  const label = categoryLabel || category.label;
  const url = new URL(categoryPath({ locale, mediaType, slug }), siteUrl).toString();
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: label,
    description: categoryDescription || category.description,
    url,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: items.length,
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: new URL(`/${locale}/post/${item.id}`, siteUrl).toString(),
        name: item.title || item.caption || `${label} ${index + 1}`,
      })),
    },
  };
}

export function canonicalExploreUrl({ siteUrl, locale = 'en', mediaType = null, slug = null } = {}) {
  const path = mediaType && slug
    ? categoryPath({ locale, mediaType, slug })
    : `/${locale}/explore`;
  return new URL(path, siteUrl).toString();
}

export function categoryBySlug(mediaType, slug) {
  return GATE_D_CATEGORIES.find((item) => item.mediaType === mediaType && item.slug === slug) || null;
}

// Content facets are controlled, multi-valued tags; primary categories remain
// a legacy scoring field, never a second public taxonomy.
export const CONTENT_FACET_GROUPS = Object.freeze({ useCases: 'use-cases', styles: 'styles', subjects: 'subjects' });
export const CONTENT_FACET_PREFIXES = Object.freeze({ useCases: 'use-case', styles: 'style', subjects: 'subject' });
export const CONTENT_FACET_PARAMS = Object.freeze({ useCases: 'useCase', styles: 'style', subjects: 'subject' });

export function contentFacet(mediaType, group, value) {
  return EXPLORE_CONTENT_FACETS[mediaType]?.[group]?.find(item => item.value === value) || null;
}

export function contentFacetTag(group, value) {
  return `${CONTENT_FACET_PREFIXES[group]}:${value}`;
}

export function contentFacetPath(mediaType, group, value) {
  if (!contentFacet(mediaType, group, value)) throw new GateDError('CONTENT_FACET_INVALID', 'Content category not found.', 404);
  return `/explore/${mediaType}/${CONTENT_FACET_GROUPS[group]}/${value}`;
}

export function parseContentFacetTags(mediaType, tags = []) {
  return Object.fromEntries(Object.entries(EXPLORE_CONTENT_FACETS[mediaType] || {}).map(([group, options]) => [
    group, options.filter(option => tags.includes(contentFacetTag(group, option.value))).map(option => option.value),
  ]));
}

export function normalizeContentFilters({ mediaType, useCase = null, style = null, subject = null } = {}) {
  const values = { useCases: useCase, styles: style, subjects: subject };
  for (const [group, value] of Object.entries(values)) {
    if (value && !contentFacet(mediaType, group, value)) {
      throw new GateDError('CONTENT_FACET_INVALID', 'The content filter does not match the media type.', 400);
    }
  }
  return { useCase: useCase || null, style: style || null, subject: subject || null };
}
